import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, FileWarning, Heart, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import API from '@/config/api';

/**
 * Reminder banner shown at the top of the Parent Dashboard.
 * Consolidates:
 *   - Overdue / upcoming Child Health Reports (per-child, from DCF 251.07(6))
 *   - Pending enrollment / intake / health forms (from magic-link tokens)
 *
 * Renders nothing if there's nothing to remind about.
 */
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const ParentRemindersBanner = () => {
  const { getAuthHeaders } = useAuth();
  const [healthReminders, setHealthReminders] = useState([]);
  const [pendingForms, setPendingForms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hr, pf] = await Promise.all([
        axios.get(`${API}/health-report-reminders`, getAuthHeaders())
          .catch(() => ({ data: { reminders: [] } })),
        axios.get(`${API}/enrollment/my-pending-forms`, getAuthHeaders())
          .catch(() => ({ data: { pending_forms: [] } })),
      ]);
      setHealthReminders(hr.data?.reminders || []);
      setPendingForms(pf.data?.pending_forms || []);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  const total = healthReminders.length + pendingForms.length;
  if (total === 0) return null;

  const overdue = healthReminders.filter(r => r.urgency === 'overdue').length;
  const headline = overdue > 0
    ? 'Action needed — please review these reminders'
    : 'A few reminders for you';

  return (
    <Card
      className={`card-clay border-l-4 ${overdue > 0 ? 'border-l-red-500 bg-red-50/40' : 'border-l-amber-500 bg-amber-50/40'}`}
      data-testid="parent-reminders-banner"
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${overdue > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
            <AlertTriangle className={`w-5 h-5 ${overdue > 0 ? 'text-red-600' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-lg">{headline}</h3>
            <p className="text-sm text-muted-foreground">
              {total} item{total === 1 ? '' : 's'} need{total === 1 ? 's' : ''} your attention.
            </p>
          </div>
        </div>

        {healthReminders.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-500" />
              <p className="text-sm font-semibold">Child Health Reports</p>
            </div>
            {healthReminders.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border"
                data-testid={`health-reminder-${r.id}`}
              >
                <div>
                  <div className="text-sm font-medium">{r.child_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.reason} · {r.form_name}
                  </div>
                </div>
                <Badge className={
                  r.urgency === 'overdue' ? 'bg-red-100 text-red-700 hover:bg-red-100'
                  : r.urgency === 'warning' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-100'
                }>
                  {r.urgency === 'overdue' ? 'Overdue' : formatDate(r.due_date)}
                </Badge>
              </div>
            ))}
            <p className="text-xs text-muted-foreground pl-1">
              Please provide an updated health report signed by your child&apos;s doctor to your center.
            </p>
          </div>
        )}

        {pendingForms.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileWarning className="w-4 h-4 text-blue-500" />
              <p className="text-sm font-semibold">Forms to Complete</p>
            </div>
            {pendingForms.map((pf) => (
              <div
                key={pf.token}
                className="flex items-center justify-between p-3 bg-white rounded-lg border"
                data-testid={`pending-form-${pf.token}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {pf.child_name || 'Enrollment'}
                    <span className="text-xs text-muted-foreground ml-2">
                      · {pf.forms_completed_count}/{pf.forms_total} completed
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {pf.forms_remaining.map(f => f.label).join(' · ')}
                  </div>
                </div>
                <Button
                  size="sm"
                  asChild
                  data-testid={`open-form-${pf.token}`}
                >
                  <a href={`/enroll/${pf.token}`}>
                    Open <ChevronRight className="w-3 h-3 ml-1" />
                  </a>
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
          <CheckCircle2 className="w-3 h-3" />
          Reminders update automatically once each item is completed.
        </div>
      </CardContent>
    </Card>
  );
};

export default ParentRemindersBanner;
